import './styles.scss'

import {
  Box,
  capitalize,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useTheme
} from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { InviteLinkRow } from 'components/badges/InviteLinkBadge'
import { CalendarHeaderTitle } from 'components/calendar/styled'
import ContainerWithBottomBadgeIcon from 'components/containers/ContainerWithBottomBadgeIcon'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { useConnection } from 'components/providers/ConnectionProvider'
import ProgressProvider, {
  useProgress
} from 'components/providers/ProgressProvider'
import TierHover from 'components/tier/TierHover'
import i18n from 'i18n'
import { t } from 'i18next'
import { GroupPrivacy, GroupVerified } from 'model/models/Group'
import {
  ChangeEvent,
  ComponentType,
  ReactNode,
  useEffect,
  useState
} from 'react'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import {
  defaultGroupImage,
  getActiveParticipants,
  isAddingMemberAllowedForGroup,
  isGroupChannel,
  isGroupOutOfTier,
  isUserActiveInGroup,
  isUserAdminInGroup,
  isUserOwnerOfGroup
} from 'utils/api/groups'
import { APP_COLOR } from 'utils/colors'

import { CloseIcon, GroupChannelIcon, Spinner } from '../../../assets/icons'
import { CircleImagePicker } from '../../../components/calendar/CircleImagePicker'
import LoadingContainer from '../../../components/containers/LoadingContainer'
import MembersList from '../../../components/lists/MembersList'
import { DialogContent } from '@mui/material'
import PopupSection from '../../../components/popup/PopupSection'
import {
  addPendingParticipantToGroup,
  generateLinkForGroup,
  resetConvertedGroupState,
  updateGroupColor,
  updateGroupPersonalSyncStatus,
  updateGroupPhoto,
  updateGroupTitle
} from '../../../services/api/group'
import ObserveService from '../../../services/db/Observe'
import {
  isOwnerForGroup,
  isUserLeft
} from '../../../services/db/utils/GroupUtils'
import LocalStorage from '../../../services/LocalStorage'
import Logger from '../../../services/Logger'
import {
  AccountDoc,
  GroupDoc,
  GroupParticipant,
  GroupParticipantStatus,
  UserSettingsDoc
} from '../../../types/api/changes'
import { Model } from '../../../types/model'
import { LocalStorageKey } from '../../../types/services/localStorage'
import { parseErrorToMessage } from '../eventInfo/EventPopup'
import * as Styled from '../styled'
import BusinessInfoSection from './BusinessInfoSection'
import ChannelToggle from './ChannelToggle'
import GroupTitle from './GroupTitle'
import InputWithAction from './InputWithAction'
import MakeGroupVerified from './MakeGroupVerified'
import MemberCanSection from './MembersCanSection'
import RemoveLinkRow from './RemoveLinkRow'
import SettingsForCalendarSection from './SettingsForCalendarSection'
import CalendarManagementSection from './CalendarManagementSection'
import {
  TabMenuContainer,
  TabMenuItem,
  TabMenuLabel,
  DialogLayout,
  TabContentContainer
} from './styled'
import EmbedCalendarDialog from 'components/dialogs/embed/EmbedCalendarDialog'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import CalendarProvider, {
  useCalendar
} from 'components/providers/CalendarProvider'
import {
  groupToProvider,
  isConvertedGroup,
  PersonalGroupSyncStatus
} from 'utils/groups'
import {
  addGroupModelToServer,
  useCalendarSync
} from 'components/providers/CalendarSyncProdivder'
import { useTour } from '@reactour/tour'
import ColorsPopup from '../ColorsPopup'
import { isTrulyEmpty } from 'utils/object'
import { RemoveThirdPartyEvents } from 'services/db/Queries/Event'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { TierFeatures } from 'utils/api/group_state'
import Database from 'services/db/Database'
import dayjs from 'dayjs'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { AddMemberByPhonePopupContent } from '../AddMemberByPhonePopup'
import { isTestEnv } from 'utils/env'
import Button from 'components/buttons/Button'
import {
  EnableLink,
  GroupDetailsEmbed,
  GroupShare
} from 'assets/icons/group-details'
import {
  GroupSideMenuCalendarM,
  GroupSideMenuPermissions,
  GroupSideMenuPersonal,
  GroupSideMenuPublic,
  GroupSideMenuSharing
} from 'assets/icons/group-details/side-menu/intex'
import Switch from 'components/form/Switch'
import ExplanationTooltip from 'components/tooltips/ExplanationTooltip'

export interface ILoading {
  setLoading: (b: boolean) => void
}

export enum GroupTab {
  PUBLIC_INFO = 'PUBLIC_INFO',
  SHARING_MEMBERS = 'SHARING_MEMBERS',
  PERSONAL_SETTINGS = 'PERSONAL_SETTINGS',
  PERMISSIONS = 'PERMISSIONS',
  CALENDAR_MANAGEMENT = 'CALENDAR_MANAGEMENT'
}

interface ContentProps {
  group: Model<GroupDoc>
  userSettings: Model<UserSettingsDoc>
  setGroupOpen: (b: boolean) => void
  account: Model<AccountDoc>
  handleClose: () => void
  openedTab?: GroupTab
}

function GroupPopupContent(props: ContentProps) {
  const theme = useTheme()
  const [selectedTab, setSelectedTab] = useState<GroupTab>(
    props.openedTab ?? GroupTab.PUBLIC_INFO
  )
  const [showEmbedDialog, onShowEmbedDialog] = useState(false)
  const hideEmbedDialog = () => {
    onShowEmbedDialog(false)

    LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
  }
  const handleShowEmbedDialog = () => {
    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
    onShowEmbedDialog(true)
  }
  const { error, setError, setLoading, loading } = useProgress()
  const connection = useConnection()
  const [editName, onEditName] = useState(false)
  const [showOutOfTierDialog, onShowOutOfTierDialog] = useState(false)

  const isUserLeftGroup =
    isUserActiveInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) === false

  const participantsToShow: Record<string, GroupParticipant> = {}

  const activeTier = highestActiveTier(props.account)
  const isOutOfTier = isGroupOutOfTier(activeTier, props.group, props.account)

  for (
    let index = 0;
    index < Object.keys(props.group.Participants ?? {}).length;
    index++
  ) {
    const key = Object.keys(props.group.Participants)[index]
    const participant = props.group.Participants[key]

    if (participant.Status === GroupParticipantStatus.ACTIVE)
      participantsToShow[key] = participant
  }

  const onPicSelected = async (pic: string) => {
    if (connection.isConnectionAvailable()) {
      setLoading(true)
      try {
        await updateGroupPhoto(pic, props.group)
      } catch (e) {
        Logger.red(String(e))
      }
      setLoading(false)
    }
  }

  const onChangeGroupName = async (title: string) => {
    if (connection.isConnectionAvailable()) {
      if (title != props.group.Name) {
        setLoading(true)
        try {
          await updateGroupTitle(title, props.group)
        } catch (e) {
          setLoading(false)
          Logger.red(String(e))
          setError(parseErrorToMessage(e))
          return false
        }
        setLoading(false)
      }
    }

    onEditName(false)
    return true
  }

  const hideErrorDialog = () => {
    setError('')
  }

  const isCurrentGroupThirdParty = isConvertedGroup(props.group)

  const canEditNameOrPhoto =
    !isOutOfTier &&
    (props.group.GroupSettings?.IsAllParticipantsCanEditGroupMetadata === '1' ||
      isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ||
      isUserOwnerOfGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      )) &&
    isUserActiveInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) &&
    props.group.VerifiedGroupStatus != GroupVerified.VERIFIED &&
    !isCurrentGroupThirdParty

  const [showColorPopup, setShowColorPopup] = useState(false)

  const renderTabMenu = () => {
    let tabs = [
      {
        id: GroupTab.PUBLIC_INFO,
        label: t('publicInformation'),
        icon: <GroupSideMenuPublic />
      },
      {
        id: GroupTab.SHARING_MEMBERS,
        label: t('sharingAndMembers'),
        icon: <GroupSideMenuSharing />
      },
      {
        id: GroupTab.PERSONAL_SETTINGS,
        label: t('personalSettings'),
        icon: <GroupSideMenuPersonal />
      },
      {
        id: GroupTab.PERMISSIONS,
        label: capitalize(t('permissions')),
        icon: <GroupSideMenuPermissions />
      },
      {
        id: GroupTab.CALENDAR_MANAGEMENT,
        label: t('calendarManagement'),
        icon: <GroupSideMenuCalendarM />
      }
    ]

    if (
      isCurrentGroupThirdParty ||
      !isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      )
    ) {
      tabs = tabs.filter((tab) => tab.id !== GroupTab.PERMISSIONS)
    }

    if (is24meApp()) {
      tabs = tabs.filter((tab) => tab.id !== GroupTab.SHARING_MEMBERS)
    }

    if (isUserLeftGroup) {
      tabs = tabs.filter(
        (tab) =>
          tab.id !== GroupTab.PERMISSIONS && tab.id !== GroupTab.SHARING_MEMBERS
      )
    }

    const managmentVisible =
      (isCurrentGroupThirdParty &&
        props.group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)) ||
      !isOwnerForGroup(props.group) ||
      isTestEnv()

    // remove calendar managment if needed
    if (!managmentVisible) {
      tabs = tabs.filter((tab) => tab.id !== GroupTab.CALENDAR_MANAGEMENT)
    }

    return (
      <TabMenuContainer
        sx={{
          '& svg': {
            width: '16px',
            height: '16px',
            minWidth: '16px',
            minHeight: '16px'
          }
        }}
      >
        {tabs.map((tab) => (
          <TabMenuItem
            key={tab.id}
            id={'group-tab-' + tab.id}
            selected={selectedTab === tab.id}
            onClick={() => setSelectedTab(tab.id)}
          >
            {tab.icon}
            <TabMenuLabel>{tab.label}</TabMenuLabel>
          </TabMenuItem>
        ))}
      </TabMenuContainer>
    )
  }

  const renderTabContent = () => {
    switch (selectedTab) {
      case GroupTab.PUBLIC_INFO:
        return (
          <>
            <PopupSection
              simplified
              popupSectionGap={'8px'}
              titlePadding="0px 0px 0px 0px"
            >
              <Styled.GroupPopupAvatarContainer>
                <div
                  style={{ pointerEvents: canEditNameOrPhoto ? 'all' : 'none' }}
                >
                  <ContainerWithBottomBadgeIcon
                    showLock={isOutOfTier}
                    topRightIcon={
                      isGroupChannel(props.group) ? (
                        <GroupChannelIcon width={18} height={18} />
                      ) : undefined
                    }
                    marginRight={-4}
                  >
                    <CircleImagePicker
                      fallback={defaultGroupImage(props.group)}
                      currentPicture={props.group.Photo}
                      onPicSelected={onPicSelected}
                    />
                  </ContainerWithBottomBadgeIcon>
                </div>
                <InputWithAction
                  disabled={!canEditNameOrPhoto}
                  initialText={props.group?.Name ?? ''}
                  editState={editName}
                  setLoading={setLoading}
                  setEditState={onEditName}
                  onSubmit={onChangeGroupName}
                  nonActiveChild={
                    <CalendarHeaderTitle className="groupTitle">
                      <GroupTitle group={props.group} iconHeight="20px" />
                    </CalendarHeaderTitle>
                  }
                />
                {canEditNameOrPhoto && (
                  <Box sx={{ marginRight: 6 }}>
                    <ColorsPopup
                      open={showColorPopup}
                      setOpen={setShowColorPopup}
                      color={props.group.GroupColor}
                      setColor={(color) => {
                        if (connection.isConnectionAvailable()) {
                          setLoading(true)
                          updateGroupColor(color, props.group)
                            .then(() => {
                              setLoading(false)
                            })
                            .catch((error) => {
                              setLoading(false)
                              Logger.red(String(error))
                              setError(parseErrorToMessage(error))
                            })
                        }
                      }}
                    />
                  </Box>
                )}
              </Styled.GroupPopupAvatarContainer>
            </PopupSection>
            <Box height={'20px'} />
            {!isCurrentGroupThirdParty && (
              <BusinessInfoSection
                group={props.group}
                setLoading={setLoading}
                account={props.account}
              />
            )}

            {isCurrentGroupThirdParty ||
            !isUserAdminInGroup(
              props.group,
              LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
            ) ? (
              <></>
            ) : (
              <>
                <Box height={'20px'} />
                <PopupSection
                  title={t('moreSettings')}
                  simplified
                  popupSectionGap={'8px'}
                >
                  <OnGroupActionSection
                    {...props}
                    activeTier={activeTier}
                    handleShowEmbedDialog={handleShowEmbedDialog}
                  />
                </PopupSection>
              </>
            )}
          </>
        )

      case GroupTab.SHARING_MEMBERS:
        return isGroupcalApp() ? <SharingMemberSection {...props} /> : <></>

      case GroupTab.PERSONAL_SETTINGS:
        return (
          <SettingsForCalendarSection
            account={props.account}
            group={props.group}
            userSettings={props.userSettings}
            setLoading={setLoading}
          />
        )

      case GroupTab.PERMISSIONS:
        if (
          (isCurrentGroupThirdParty &&
            props.group.PersonalGroupSyncStatus !==
              PersonalGroupSyncStatus.SYNCED) ||
          isUserLeftGroup ||
          !isUserAdminInGroup(
            props.group,
            LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
          )
        ) {
          return <></>
        }

        return isUserLeftGroup ? (
          <Typography
            color={'var(--color-negative)'}
            align={'center'}
            padding={2}
          >
            {isUserLeft(
              props.group,
              LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
            )
              ? t('youLeft')
              : t('youRemovedFromCalendar')}
          </Typography>
        ) : (
          <MemberCanSection
            error={error}
            account={props.account}
            setError={setError}
            group={props.group}
            setLoading={setLoading}
          />
        )

      case GroupTab.CALENDAR_MANAGEMENT:
        return <CalendarManagementSection {...props} />

      default:
        return null
    }
  }

  return (
    <Box
      id="group-details-popup"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%'
      }}
    >
      <LoadingContainer loading={loading ? 1 : 0} />
      <IconButton
        id="groupPopupCloseButton"
        sx={{
          position: loading ? 'none' : 'absolute',
          top: 8,

          right: i18n.dir() === 'rtl' ? 'unset' : 8,
          left: i18n.dir() === 'rtl' ? 8 : 'unset',
          display: loading ? 'none' : 'inline-flex',
          width: 32,
          height: 32,
          minWidth: 32,
          minHeight: 32,
          padding: 0,
          borderRadius: '50%',
          zIndex: 999999
        }}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          props.handleClose()
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogLayout>
        {renderTabMenu()}
        <TabContentContainer>{renderTabContent()}</TabContentContainer>
      </DialogLayout>
      {error && (
        <ConfirmationDialog
          title={t('cantDoThat')}
          content={error}
          rightButtonHidden
          leftButton={`${t('ok')}`}
          leftButtonColor={APP_COLOR}
          handleLeftButton={hideErrorDialog}
          open={error.length > 0}
          handleClose={hideErrorDialog}
        />
      )}
      {showEmbedDialog && (
        <CalendarProvider>
          <EmbedCalendarDialog
            account={props.account}
            group={props.group}
            userSettings={props.userSettings}
            open={showEmbedDialog}
            handleClose={hideEmbedDialog}
          />
        </CalendarProvider>
      )}
      <TierHover
        isOutOfTier={isOutOfTier}
        topOffset={40}
        leftOffset={240}
        account={props.account}
        group={props.group}
        opened={showOutOfTierDialog}
        setOpen={onShowOutOfTierDialog}
      />
    </Box>
  )
}

interface OnGroupActionsProps extends ContentProps {
  activeTier: Tier
  handleShowEmbedDialog?: () => void
}

function OnGroupActionSection(props: OnGroupActionsProps) {
  const { setError, setLoading } = useProgress()

  if (
    !isUserAdminInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )
  ) {
    return <></>
  }
  return (
    <>
      {isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ? (
        <Box display={'flex'} flexDirection={'column'} gap="8px">
          <ChannelToggle group={props.group} setLoading={setLoading} />
          <MakeGroupVerified
            currentTier={props.activeTier}
            group={props.group}
          />
        </Box>
      ) : (
        <></>
      )}
      {props.group.PrivateLinkUrl && (
        <PopupSectionRow
          actionId="embed-button"
          onlyTitle
          icon={<GroupDetailsEmbed />}
          iconClass="icon-xs"
          explanation={t('embedMultipleCalendarsDesc')}
          title={t('embedCalendar')}
          value={<Button size="xs">{t('configure')}</Button>}
          action={props.handleShowEmbedDialog}
        />
      )}
    </>
  )
}

function MembersSection(props: ContentProps) {
  const { setError, setLoading } = useProgress()
  const [addedMemberPhone, setAddedMemberPhone] = useState<string | undefined>(
    undefined
  )

  const shouldShowParticipants =
    props.group.PrivacyMode === GroupPrivacy.PRIVATE ||
    (props.group.PrivacyMode === GroupPrivacy.CHANNEL &&
      (isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ||
        props.group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID))) ||
    isTrulyEmpty(props.group.CalendarEmail) === false

  let shouldShowLink = false

  if (!isTrulyEmpty(props.group.PrivateLinkUrl)) {
    if (isAddingMemberAllowedForGroup(props.group)) {
      shouldShowLink = true
    } else {
      shouldShowLink =
        isOwnerForGroup(props.group) ||
        isUserAdminInGroup(
          props.group,
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        )
    }
  } else {
    shouldShowLink =
      isOwnerForGroup(props.group) ||
      isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      )
  }

  return (
    <Box display={'flex'} flexDirection={'column'}>
      {shouldShowLink && (
        <>
          <PopupSection
            simplified
            padding="8px 0px 0px 0px"
            title={t('shareThisCalendar')}
            titleIcon={
              <ExplanationTooltip title={t('shareThisCalendarDesc')} />
            }
          >
            <>
              {props.group.PrivateLinkUrl ? (
                <LinkActions group={props.group} />
              ) : isUserAdminInGroup(
                  props.group,
                  LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
                ) ? (
                <AddLinkContainer {...props} />
              ) : (
                <></>
              )}
            </>
          </PopupSection>
          <Box height={'20px'} />
        </>
      )}

      <>
        {!isTrulyEmpty(props.group?._rev) &&
          isAddingMemberAllowedForGroup(props.group) &&
          props.group.PersonalGroupSyncStatus !==
            PersonalGroupSyncStatus.PRIVATE && (
            <PopupSection
              simplified
              popupSectionGap={'8px'}
              padding="8px 0px 0px 0px"
              title={t('addMembersByPhoneNumbers')}
              titleIcon={
                <ExplanationTooltip title={t('addMembersByPhoneNumbersDesc')} />
              }
            >
              <AddMemberByPhonePopupContent
                setOpen={() => {}}
                onAddMember={(phone) => {
                  setLoading(true)
                  addPendingParticipantToGroup(phone, props.group)
                    .then(() => {
                      setLoading(false)
                      setAddedMemberPhone(phone)
                    })
                    .catch((error) => {
                      setLoading(false)
                      setError(parseErrorToMessage(error))
                    })
                }}
              />
            </PopupSection>
          )}
        <Box height={'20px'} />
      </>
      <PopupSection
        sectionId="membersSection"
        popupSectionGap={'8px'}
        title={`${t('membersInGroup', {
          memberCount: Object.keys(
            getActiveParticipants(props.group.Participants)
          ).length
        })}`}
        simplified
      >
        {shouldShowParticipants ? (
          <>
            {props.group.Participants != null && shouldShowParticipants ? (
              <MembersList
                group={props.group}
                groupId={props.group._id}
                setLoading={setLoading}
                participants={props.group.Participants}
                withoutActions={false}
                localParticipants={[]}
                addedMemberPhone={addedMemberPhone}
                noExpander
              />
            ) : (
              <></>
            )}
          </>
        ) : props.group.PrivateLinkUrl &&
          props.group.PrivacyMode === GroupPrivacy.CHANNEL ? (
          <InviteLinkRow link={props.group.PrivateLinkUrl} />
        ) : (
          <></>
        )}
      </PopupSection>
    </Box>
  )
}

function AddLinkContainer(props: ContentProps) {
  const connection = useConnection()
  const { setError, setLoading } = useProgress()

  const [showGroupSharingExplanation, setShowGroupSharingExplanation] =
    useState(false)

  const updateGroupWithLink = async () => {
    if (connection.isConnectionAvailable()) {
      setLoading(true)

      if (isConvertedGroup(props.group)) {
        // create a group here
        setShowGroupSharingExplanation(true)
      } else {
        try {
          const updated = await generateLinkForGroup(props.group)

          try {
            await navigator.clipboard.writeText(updated.PrivateLinkUrl)
          } catch (e) {}
        } catch (e) {
          setError(parseErrorToMessage(e))
        }
      }

      setLoading(false)
    }
  }

  const calendarSync = useCalendarSync()
  const calendar = useCalendar()

  const handlePersonalCalenadrShare = async () => {
    setShowGroupSharingExplanation(false)
    setLoading(true)

    try {
      if (isTrulyEmpty(props.group._rev)) {
        await RemoveThirdPartyEvents(props.group._id)
          .then(() => {
            return addGroupModelToServer(props.group).then((group) => {
              calendar.onGroupServer(group._id ?? '')
            })
          })
          .then(() => {
            const provider = groupToProvider(props.group)
            if (provider) {
              return calendarSync.sync(provider).then(() => {
                return syncLocalCalendarsToServer(true)
              })
            }
          })
          .then(() => {
            return updateGroupPersonalSyncStatus(
              PersonalGroupSyncStatus.SYNCED,
              props.group
            )
          })
          .then(() => {
            Database.write(async () => {
              props.group.update((local) => {
                local._raw['_sortOrder'] = dayjs().unix()
                local.PersonalGroupSyncStatus = PersonalGroupSyncStatus.SYNCED
                local.LastUpdateMessage = {
                  type: t('youSharedThisCalendar')
                }
              })
            })
          })
      } else {
        await generateLinkForGroup(props.group).then(() => {
          return RemoveThirdPartyEvents(props.group._id).then(() => {
            const provider = groupToProvider(props.group)
            if (provider) {
              ;(props.group.PersonalGroupSyncStatus !==
              PersonalGroupSyncStatus.SYNCED
                ? updateGroupPersonalSyncStatus(
                    PersonalGroupSyncStatus.SYNCED,
                    props.group
                  )
                : Promise.resolve()
              )
                .then(() => {
                  return Database.write(async () => {
                    props.group.update((local) => {
                      local._raw['_sortOrder'] = dayjs().unix()
                      local.PersonalGroupSyncStatus =
                        PersonalGroupSyncStatus.SYNCED
                      local.LastUpdateMessage = {
                        type: t('youSharedThisCalendar')
                      }
                    })
                  })
                })
                .then(() => {
                  return calendarSync.sync(provider)
                })
                .then(() => {
                  return syncLocalCalendarsToServer(true)
                })
            }
          })
        })
      }
    } catch (e) {
      console.error(e)
      setError(parseErrorToMessage(e))
    }

    setLoading(false)
  }
  return (
    <>
      <PopupSectionRow
        onlyTitle
        account={props.account}
        title={t(
          isConvertedGroup(props.group)
            ? 'sharePersonalCalendarLink'
            : 'shareCalendarWithLink'
        )}
        icon={<EnableLink />}
        iconClass="icon-xs"
        value={<Button size="xs">{t('addLink')}</Button>}
        tierFeature={TierFeatures.SHARE_PERSONAL_CALENDAR}
        neededTier={
          !isTrulyEmpty(props.group.CalendarEmail) ? Tier.PRO : Tier.FREE
        }
        actionId="share-calendar"
        action={() => {
          updateGroupWithLink()
        }}
      />
      <ConfirmationDialog
        title={`${t('sharePersonalCalendarTitle')}`}
        content={`${t('sharePersonalCalendarDesc')}`}
        rightButton={`${t('shareLabel')}`}
        leftButton={`${t('cancel')}`}
        handleRightButton={() => {
          handlePersonalCalenadrShare()
        }}
        handleLeftButton={() => {
          setShowGroupSharingExplanation(false)
        }}
        handleClose={() => {
          setShowGroupSharingExplanation(false)
        }}
        open={showGroupSharingExplanation}
      />
    </>
  )
}
interface LinkActionsProps {
  group: Model<GroupDoc>
}

function LinkActions(props: LinkActionsProps) {
  return (
    <Box display={'flex'} flexDirection={'column'} gap="8px">
      <InviteLinkRow link={props.group.PrivateLinkUrl} />
      {isUserAdminInGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ||
      isUserOwnerOfGroup(
        props.group,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) ? (
        <RemoveLinkRow group={props.group} />
      ) : (
        <></>
      )}
    </Box>
  )
}

interface GroupPopupProps {
  setOpen: (b: boolean) => void
  isOpen: boolean
  children: ReactNode
  group: Model<GroupDoc>
  groupID: string
  userSettings: Model<UserSettingsDoc>
  account: Model<AccountDoc>
  openedTab?: GroupTab
}

function SharingMemberSection(props: ContentProps) {
  const theme = useTheme()
  const connection = useConnection()
  const { setError, setLoading } = useProgress()

  const [showGroupSharingExplanation, setShowGroupSharingExplanation] =
    useState(false)
  const calendarSync = useCalendarSync()
  const calendar = useCalendar()

  const [personalGroupCreating, setPersonalGroupCreating] = useState(false)

  const handlePersonalCalenadrShare = async () => {
    setShowGroupSharingExplanation(false)
    setLoading(true)
    setPersonalGroupCreating(true)

    try {
      if (isTrulyEmpty(props.group._rev)) {
        await RemoveThirdPartyEvents(props.group._id)
          .then(() => {
            return addGroupModelToServer(props.group).then((group) => {
              calendar.onGroupServer(group._id ?? '')
            })
          })
          .then(() => {
            const provider = groupToProvider(props.group)
            if (provider) {
              return calendarSync.sync(provider).then(() => {
                return syncLocalCalendarsToServer(true)
              })
            }
          })
          .then(() => {
            return updateGroupPersonalSyncStatus('2', props.group)
          })
          .then(() => {
            LocalStorage.remove(LocalStorageKey.ADDING_GROUP)
            Database.write(async () => {
              props.group.update((local) => {
                local._raw['_sortOrder'] = dayjs().unix()
                local.LastUpdateMessage = {
                  type: t('youSharedThisCalendar')
                }
              })
            })
          })
      } else {
        await generateLinkForGroup(props.group).then(() => {
          return RemoveThirdPartyEvents(props.group._id).then(() => {
            const provider = groupToProvider(props.group)
            if (provider) {
              ;(props.group.PersonalGroupSyncStatus !==
              PersonalGroupSyncStatus.SYNCED
                ? updateGroupPersonalSyncStatus(
                    PersonalGroupSyncStatus.SYNCED,
                    props.group
                  )
                : Promise.resolve()
              )
                .then(() => {
                  return Database.write(async () => {
                    props.group.update((local) => {
                      local._raw['_sortOrder'] = dayjs().unix()
                      local.PersonalGroupSyncStatus =
                        PersonalGroupSyncStatus.SYNCED
                      local.LastUpdateMessage = {
                        type: t('youSharedThisCalendar')
                      }
                    })
                  })
                })
                .then(() => {
                  return calendarSync.sync(provider)
                })
                .then(() => {
                  return syncLocalCalendarsToServer(true)
                })
            }
          })
        })
      }
    } catch (e) {
      console.error(e)
      setError(parseErrorToMessage(e))
    }

    setPersonalGroupCreating(false)
    setLoading(false)
  }

  const syncPersonalGroup = (event: ChangeEvent<HTMLInputElement>) => {
    if (highestActiveTier(props.account) < Tier.PRO) return

    const value = event.target.checked

    if (value) {
      setShowGroupSharingExplanation(true)
    } else {
      resetConvertedGroupState(props.group)
    }
  }

  Logger.blue(
    'SharingMember:PersonalGroupSyncStatus',
    props.group.PersonalGroupSyncStatus
  )

  return (
    <>
      {isConvertedGroup(props.group) && (
        <>
          <PopupSectionRow
            title={t('shareThisPersonalCalendar')}
            explanation={t('sharePersonalCalendarDesc')}
            account={props.account}
            maxWidthIcon={24}
            value={
              personalGroupCreating ||
              props.group.PersonalGroupSyncStatus ===
                PersonalGroupSyncStatus.PENDING ||
              !isTrulyEmpty(LocalStorage.get(LocalStorageKey.ADDING_GROUP)) ? (
                <Spinner
                  style={{
                    animation: 'spin 1s linear infinite',
                    maxWidth: 'undefined !important',
                    width: '24px !important',
                    height: '24px !important'
                  }}
                />
              ) : (
                <Switch
                  id="toggleSharePersonal"
                  checked={
                    props.group.PersonalGroupSyncStatus ===
                    PersonalGroupSyncStatus.SYNCED
                  }
                  onChange={syncPersonalGroup}
                />
              )
            }
            neededTier={
              !isTrulyEmpty(props.group.CalendarEmail) ? Tier.PRO : Tier.FREE
            }
            tierFeature={TierFeatures.SHARE_PERSONAL_CALENDAR}
            iconClass="icon-xs"
            icon={<GroupShare />}
          />
          <ConfirmationDialog
            title={`${t('sharePersonalCalendarTitle')}`}
            content={`${t('sharePersonalCalendarDesc')}`}
            rightButton={`${t('shareLabel')}`}
            leftButton={`${t('cancel')}`}
            handleRightButton={() => {
              handlePersonalCalenadrShare()
            }}
            handleLeftButton={() => {
              setShowGroupSharingExplanation(false)
            }}
            handleClose={() => {
              setShowGroupSharingExplanation(false)
            }}
            open={showGroupSharingExplanation}
          />
          <Divider
            sx={{
              marginTop: 4,
              marginBottom: '20px',
              backgroundColor: theme.palette.border.primary,
              borderColor: 'var(--color-border-overlay)'
            }}
          />
        </>
      )}

      <>
        {((isConvertedGroup(props.group) &&
          props.group.PersonalGroupSyncStatus ===
            PersonalGroupSyncStatus.SYNCED) ||
          !isConvertedGroup(props.group)) && <MembersSection {...props} />}
      </>
    </>
  )
}

function GroupPopup(props: GroupPopupProps) {
  const theme = useTheme()
  const calendar = useCalendar()
  const tour = useTour()
  const { currentStep, setCurrentStep, setIsOpen } = useTour()

  const stopCloseEvent = (event?: {
    stopPropagation?: () => void
    preventDefault?: () => void
  }) => {
    event?.stopPropagation?.()
    event?.preventDefault?.()
  }

  useEffect(() => {
    if (props.isOpen)
      LocalStorage.set(LocalStorageKey.SHOWING_GROUP_INFO, props.groupID)
    else LocalStorage.remove(LocalStorageKey.SHOWING_GROUP_INFO)
  }, [props.isOpen])

  const handleClose = (event?: {
    stopPropagation?: () => void
    preventDefault?: () => void
  }) => {
    stopCloseEvent(event)
    Logger.blue('GroupPopup:handleClose', calendar)
    if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1') return

    LocalStorage.remove(LocalStorageKey.SHOWING_GROUP_INFO)
    calendar.onGroupInfo(undefined)
    props.setOpen(false)
    LocalStorage.remove(LocalStorageKey.ADDING_GROUP)
    Logger.blue('GroupPopup:handleClose:after', calendar)
    if (
      (LocalStorage.get(LocalStorageKey.USER_FINSHED_ONBOARDING) ?? '0') === '0'
    ) {
      setCurrentStep(currentStep + 1)
      setIsOpen(true)
    }
  }

  return (
    <ProgressProvider>
      {props.children}
      <Dialog
        open={props.isOpen}
        onClose={(event) => {
          handleClose(event)
        }}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '20px',
            maxHeight: 'min(90vh, 1000px)',
            display: 'flex',
            flexDirection: 'column',
            width: 'fit-content',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            boxSizing: 'border-box',
            backgroundColor: 'var(--color-surface-subtle)'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <GroupPopupContent
              setGroupOpen={props.setOpen}
              handleClose={handleClose}
              {...props}
            />
          </div>
        </DialogContent>
      </Dialog>
    </ProgressProvider>
  )
}
const GroupPopupWithGroup = withObservables(['groupID', 'userSettings'], (props: GroupPopupProps) => ({
  group: ObserveService.GroupByServerID(props.groupID)
}))(GroupPopup as ComponentType<unknown>)

const GroupPopupWithUserSettings = withObservables([], () => ({
  userSettings: ObserveService.UserSettings()
}))(GroupPopupWithGroup as any)

export default withObservables([], () => ({
  account: ObserveService.Account()
}))(GroupPopupWithUserSettings as any) as any
